/**
 * Dataverse Quote Types - Matches actual QuoteApi response
 */

import { IDealersCurrency } from '@/types';

export interface IDataverseQuote {
  "@odata.etag"?: string;
  quoteid: string;
  name: string;
  quotenumber?: string;
  description?: string | null;
  effectivefrom?: string | null;
  effectiveto?: string | null;
  expireson?: string | null;
  totalamount?: number;
  totalamount_base?: number;
  totallineitemamount?: number;
  totallineitemamount_base?: number;
  totaltax?: number;
  totaltax_base?: number;
  totaldiscountamount: number;
  totaldiscountamount_base?: number;
  dogma_discountamount?: number;
  dogma_discountpercentage?: number;
  statuscode: number;
  statecode?: number;
  createdon?: string;
  modifiedon?: string;
  closedon?: string;
  revisionnumber?: number;
  versionnumber?: number;

  // Customer expanded data
  customerid_contact?: {
    firstname: string;
    lastname: string;
    address1_line1: string;
    address1_postalcode: string;
    contactid: string;
    emailaddress1: string;
    telephone1: string;
    statuscode: number;
    statecode?: number;
    dogma_marginpercentage?: number;
    dogma_discountfromvsbaseprice?: number;
    "@odata.etag"?: string;
  };
  customerid_account?: {
    accountid?: string;
    dogma_discountfromvsbaseprice?: number;
    dogma_marginpercentage?: number;
    dogma_deliverycharge?: boolean;
    name?: string;
    paymenttermscode?: number;
    telephone1?: string | null;
    fax?: string | null;
  };

  dogma_customer?: {
    accountid?: string;
    name?: string;
    address1_composite?: string | null;
    telephone1?: string | null;
    fax?: string | null;
    primarycontactid?: {
      fullname?: string | null;
      emailaddress1?: string | null;
      telephone1?: string | null;
    };
  };

  // Customer references
  _customerid_value?: string | null;
  "_customerid_value@OData.Community.Display.V1.FormattedValue"?: string;
  _accountid_value?: string | null;
  _contactid_value?: string | null;

  // Opportunity reference
  _opportunityid_value?: string | null;
  "_opportunityid_value@OData.Community.Display.V1.FormattedValue"?: string;

  // Price level
  _pricelevelid_value?: string | null;
  "_pricelevelid_value@OData.Community.Display.V1.FormattedValue"?: string;

  // Currency
  _transactioncurrencyid_value?: string | null;
  "_transactioncurrencyid_value@OData.Community.Display.V1.FormattedValue"?: string;

  // Owner references
  _ownerid_value?: string;
  "_ownerid_value@OData.Community.Display.V1.FormattedValue"?: string;
  _owninguser_value?: string;
  _owningbusinessunit_value?: string;
  _owningteam_value?: string | null;

  "_dogma_customer_value@OData.Community.Display.V1.FormattedValue": string;
  // Created/Modified by
  _createdby_value?: string;
  "_createdby_value@OData.Community.Display.V1.FormattedValue"?: string;
  _modifiedby_value?: string;
  "_modifiedby_value@OData.Community.Display.V1.FormattedValue"?: string;

  // Billing address
  billto_name?: string | null;
  billto_line1?: string | null;
  billto_line2?: string | null;
  billto_line3?: string | null;
  billto_city?: string | null;
  billto_stateorprovince?: string | null;
  billto_country?: string | null;
  billto_postalcode?: string | null;
  billto_telephone?: string | null;
  billto_fax?: string | null;

  // Shipping address
  shipto_name?: string | null;
  shipto_line1?: string | null;
  shipto_line2?: string | null;
  shipto_line3?: string | null;
  shipto_city?: string | null;
  shipto_stateorprovince?: string | null;
  shipto_country?: string | null;
  shipto_postalcode?: string | null;
  shipto_telephone?: string | null;
  shipto_fax?: string | null;
  dogma_exchangerate?: number;
  dogma_marginpercentage?: number;
  dogma_region?: number;
  dogma_DealersCurrency?: IDealersCurrency;
  // Formatted values
  "statuscode@OData.Community.Display.V1.FormattedValue"?: string;
  "statecode@OData.Community.Display.V1.FormattedValue"?: string;
  "totalamount@OData.Community.Display.V1.FormattedValue"?: string;
  "totaltax@OData.Community.Display.V1.FormattedValue"?: string;
  "createdon@OData.Community.Display.V1.FormattedValue"?: string;
  "modifiedon@OData.Community.Display.V1.FormattedValue"?: string;
  "closedon@OData.Community.Display.V1.FormattedValue"?: string;
  "dogma_projecttype@OData.Community.Display.V1.FormattedValue"?: string;
  "totaldiscountamount@OData.Community.Display.V1.FormattedValue": string;
  // Other fields
  emailaddress?: string | null;
  requestdeliveryby?: string | null;
  freightamount?: number | null;
  freightamount_base?: number | null;
  discountamount?: number | null;
  discountamount_base?: number | null;
  discountpercentage?: number | null;
  paymenttermscode?: number | null;
  freighttermscode?: number | null;
  shippingmethodcode?: number | null;
  willcall?: boolean;
  quotecreationmethod?: number;
  pricingerrorcode?: number;
  exchangerate?: number;
  dogma_projectreference?: string;
  dogma_followup?: number; // QuoteFollowUpStatus: 0=None, 1=In Progress, 2=Resolved
  quoteDetails?: IQuoteLineItem[];

  //NEW VSS FIELD
  dogma_vssdiscountamount?: number;
  "dogma_vssdiscountamount@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vssdiscountpercentage?: number;
  dogma_vsssubtotal?: number;
  "dogma_vsssubtotal@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vsstotalamount?: number;
  "dogma_vsstotalamount@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vssvatamount?: number;
  "dogma_vssvatamount@OData.Community.Display.V1.FormattedValue"?: string;

  [key: string]: any; // Allow for additional dynamic fields
}

export interface IDataverseQuoteForm {
  name: string;
  quotenumber?: string;
  description?: string;
  effectivefrom?: string;
  effectiveto?: string;
  totalamount?: number;
  customerid?: string;
  statuscode?: number;
}

export interface IQuoteQueryParams {
  current: number;
  pageSize: number;
  quoteFilter?: string;
  subProductFilter?: string;
  statusFilter?: number;
  nextSkipToken?: string;
}

export enum QuoteStateCode {
  DRAFT = 0,
  ACTIVE = 1,
  WON = 2,
  Closed = 3,
}

export enum QuoteFollowUpStatus {
  NONE = 0,
  IN_PROGRESS = 1,
  RESOLVED = 2,
}

export enum QuoteStatus {
  FOLLOW_UP_NEEDED = 1,
  LIVE = 2,
  CONVERTED = 4,
  LOST = 5,
  EXPIRED = 6,
  REVISED = 7,
  SIGNED = 736000001,
  DRAFT = 736000002,
}

export interface IQuoteStats {
  inProgress: number;
  draft: number;
  open: number;
  won: number;
  lost: number;
  canceled: number;
  revised: number;
  total: number;
}

export interface IQuoteStatusCount {
  "statuscode@OData.Community.Display.V1.FormattedValue": string;
  statuscode: number;
  "count@OData.Community.Display.V1.FormattedValue": string;
  count: number;
}

export interface IQuoteStatusCountResponse {
  value: IQuoteStatusCount[];
}

export interface QuoteImageItem {
  fileName: string;
  imageUrl: string;
}

export interface QuoteImageResponse {
  success: boolean;
  containerName: string;
  quoteGuid: string;
  location: string;
  imageCount: number;
  images: QuoteImageItem[];
}

export interface IQuoteCountResponse {
  "@odata.context"?: string;
  value: {
    "count@OData.Community.Display.V1.FormattedValue"?: string;
    count: number;
  }[];
}

export interface IQuoteTotalValueResponse {
  "@odata.context"?: string;
  value: {
    "totalValue@OData.Community.Display.V1.FormattedValue"?: string;
    totalValue: number;
  }[];
}

/**
 * Payload for converting quote to order
 */
export interface ICreateOrderPayload {
  quoteid: string;
  dogma_paymentstatus: number;
  description?: string;
  requestdeliveryby?: string;
}

/**
 * Payload for closing quote with status and reason
 */
export interface ICloseQuotePayload {
  quoteid: string;
  subject?: string;
  Status: number;
}

/**
 * Activity Log Types
 */
export interface IActivityLog {
  "@odata.etag"?: string;
  activityid: string;
  _regardingobjectid_value?: string;
  "_regardingobjectid_value@OData.Community.Display.V1.FormattedValue"?: string;
  subject: string;
  description?: string | null;
  createdon?: string;
}

export interface IActivityLogResponse {
  "@odata.context": string;
  value: IActivityLog[];
}

/**
 * Quote Line Item Types
 */
export interface IQuoteLineItem {
  "@odata.etag"?: string;
  quotedetailid: string;
  quotedetailname?: string;
  productname?: string;
  productdescription?: string;
  quantity?: number;
  priceperunit?: number;
  priceperunit_base?: number;
  baseamount?: number;
  baseamount_base?: number;
  extendedamount?: number;
  extendedamount_base?: number;
  manualdiscountamount?: number;
  manualdiscountamount_base?: number;
  tax?: number;
  tax_base?: number;
  _quoteid_value?: string;
  _productid_value?: string;
  _uomid_value?: string;
  lineitemnumber?: number;
  sequencenumber?: number;
  ispriceoverridden?: boolean;
  isproductoverridden?: boolean;
  volumediscountamount?: number;
  volumediscountamount_base?: number;
  dogma_windowname?: string;
  subProducts?: ISubQuoteProduct[];

  // Formatted values
  "quantity@OData.Community.Display.V1.FormattedValue"?: string;
  "priceperunit@OData.Community.Display.V1.FormattedValue"?: string;
  "baseamount@OData.Community.Display.V1.FormattedValue"?: string;
  "extendedamount@OData.Community.Display.V1.FormattedValue"?: string;
  "manualdiscountamount@OData.Community.Display.V1.FormattedValue"?: string;
  "tax@OData.Community.Display.V1.FormattedValue"?: string;

  // VSS fields
  dogma_vssdiscountamount?: number;
  "dogma_vssdiscountamount@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vssdiscountpercentage?: number;
  dogma_vsssubtotal?: number;
  "dogma_vsssubtotal@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vsstotalamount?: number;
  "dogma_vsstotalamount@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vssvatamount?: number;
  "dogma_vssvatamount@OData.Community.Display.V1.FormattedValue"?: string;

  [key: string]: any;
}

export interface IQuoteListResponse {
  success: boolean;
  message: string;
  count: number;
  data: IDataverseQuote[];
  timestamp: string;
  current?: number;
  pageSize?: number;
  total?: number;
  nextLink?: string;
  nextSkipToken?: string;
  totalRecordCountLimitExceeded?: boolean;
}

export interface IQuoteLineItemsResponse {
  "@odata.context"?: string;
  value: IQuoteLineItem[];
}

/**
 * Sub Quote Product Types
 */
export interface ISubQuoteProduct {
  "@odata.etag"?: string;
  dogma_subquoteproductid: string;
  dogma_name: string;
  dogma_quantity: number;
  dogma_amount: number;
  dogma_amount_base?: number;
  _dogma_quoteproduct_value?: string;
  _dogma_product_value?: string;
  createdon?: string;
  modifiedon?: string;
  dogma_step?: number;
  dogma_optiontype?: number;
  dogma_optionname?: string;
  dogma_categoryname?: string;
  dogma_value?: string;
  dogma_questionjson?: string;

  dogma_vssprice?: number;
  "dogma_vssprice@OData.Community.Display.V1.FormattedValue"?: string;
  dogma_vssdiscountamount?: number;
  "dogma_vssdiscountamount@OData.Community.Display.V1.FormattedValue"?: string;

  // Formatted values
  "dogma_amount@OData.Community.Display.V1.FormattedValue"?: string;
  "dogma_quantity@OData.Community.Display.V1.FormattedValue"?: string;
  "createdon@OData.Community.Display.V1.FormattedValue"?: string;
  "modifiedon@OData.Community.Display.V1.FormattedValue"?: string;
  "_dogma_quoteproduct_value@OData.Community.Display.V1.FormattedValue"?: string;

  [key: string]: any;
}

export interface ISubQuoteProductsResponse {
  "@odata.context"?: string;
  value: ISubQuoteProduct[];
}
